/**
 * Morning (Green Invoice) Integration Service
 *
 * This service provides utilities and constants for integrating
 * with the Morning (formerly Green Invoice) invoicing system.
 *
 * API Documentation: https://developers.morning.co/api (OpenAPI: /docs/openapi.bundled.json)
 *
 * Cloud Functions handle the actual API communication (see /functions/morningIntegration.js)
 */

// ==============================================
// CONSTANTS
// ==============================================

/**
 * Morning API Base URLs
 */
export const MORNING_API_URLS = {
  PRODUCTION: "https://api.greeninvoice.co.il/api/v1",
  SANDBOX: "https://sandbox.d.greeninvoice.co.il/api/v1",
};

/**
 * Morning Document Types
 * These are the official document type codes from Morning API
 */
// Verified against Morning's OpenAPI spec (developers.morning.co, 2026-10-29).
// ⚠️ Before 2026-10-29 the first two values were SWAPPED (305 was labelled
// "חשבונית מס קבלה"). Settings saved by that code carry no `settingsVersion`
// and are remapped by `resolveClinicDefaultDocumentType` / the server.
export const MORNING_DOCUMENT_TYPES = {
  TAX_INVOICE: 305, // חשבונית מס
  TAX_INVOICE_RECEIPT: 320, // חשבונית מס / קבלה - Most common for clinics
  RECEIPT: 400, // קבלה - the only option for עוסק פטור
  CREDIT_INVOICE: 330, // חשבונית זיכוי
  PROFORMA: 300, // חשבון עסקה
  PRICE_QUOTE: 10, // הצעת מחיר
  DELIVERY_NOTE: 200, // תעודת משלוח
};

/** Settings written by the current (correct) code carry this version. */
export const MORNING_SETTINGS_VERSION = 2;

/**
 * Hebrew labels for document types
 */
export const MORNING_DOCUMENT_LABELS = {
  [MORNING_DOCUMENT_TYPES.TAX_INVOICE]: "חשבונית מס",
  [MORNING_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT]: "חשבונית מס / קבלה",
  [MORNING_DOCUMENT_TYPES.RECEIPT]: "קבלה",
  [MORNING_DOCUMENT_TYPES.CREDIT_INVOICE]: "חשבונית זיכוי",
  [MORNING_DOCUMENT_TYPES.PROFORMA]: "חשבון עסקה",
  [MORNING_DOCUMENT_TYPES.PRICE_QUOTE]: "הצעת מחיר",
  [MORNING_DOCUMENT_TYPES.DELIVERY_NOTE]: "תעודת משלוח",
};

/** Short hint shown under the document-type select. */
export const MORNING_DOCUMENT_HINTS = {
  [MORNING_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT]: "חשבונית וקבלה במסמך אחד, נרשם גם התשלום",
  [MORNING_DOCUMENT_TYPES.TAX_INVOICE]: "חשבונית בלבד, ללא רישום תשלום (החשבונית תישאר פתוחה במורנינג)",
  [MORNING_DOCUMENT_TYPES.RECEIPT]: "קבלה בלבד, מתאים לעוסק פטור",
  [MORNING_DOCUMENT_TYPES.CREDIT_INVOICE]: "זיכוי כנגד חשבונית קודמת",
};

/**
 * Morning Payment Types
 * These are the official payment type codes from Morning API
 */
export const MORNING_PAYMENT_TYPES = {
  CASH: 1, // מזומן
  CHECK: 2, // צ'ק
  CREDIT_CARD: 3, // כרטיס אשראי
  BANK_TRANSFER: 4, // העברה בנקאית
  PAYPAL: 5, // PayPal
  OTHER: 11, // אחר
  BIT: 10, // ביט
};

/**
 * Hebrew labels for payment types
 */
export const MORNING_PAYMENT_LABELS = {
  [MORNING_PAYMENT_TYPES.CASH]: "מזומן",
  [MORNING_PAYMENT_TYPES.CHECK]: "צ'ק",
  [MORNING_PAYMENT_TYPES.CREDIT_CARD]: "כרטיס אשראי",
  [MORNING_PAYMENT_TYPES.BANK_TRANSFER]: "העברה בנקאית",
  [MORNING_PAYMENT_TYPES.PAYPAL]: "PayPal",
  [MORNING_PAYMENT_TYPES.OTHER]: "אחר",
  [MORNING_PAYMENT_TYPES.BIT]: "אפליקציה",
};

/**
 * Business Types
 */
export const MORNING_BUSINESS_TYPES = {
  OSEK_PATUR: "osekPatur", // עוסק פטור
  OSEK_MORSHE: "osekMorshe", // עוסק מורשה
  COMPANY: "company", // חברה בע"מ
  AMUTA: "amuta", // עמותה
  PUBLIC_BENEFIT: "publicBenefit", // חברה לתועלת הציבור
  PARTNERSHIP: "partnership", // שותפות
};

/**
 * Hebrew labels for business types
 */
export const MORNING_BUSINESS_TYPE_LABELS = {
  [MORNING_BUSINESS_TYPES.OSEK_PATUR]: "עוסק פטור",
  [MORNING_BUSINESS_TYPES.OSEK_MORSHE]: "עוסק מורשה",
  [MORNING_BUSINESS_TYPES.COMPANY]: "חברה בע״מ",
  [MORNING_BUSINESS_TYPES.AMUTA]: "עמותה",
  [MORNING_BUSINESS_TYPES.PUBLIC_BENEFIT]: "חברה לתועלת הציבור",
  [MORNING_BUSINESS_TYPES.PARTNERSHIP]: "שותפות",
};

/**
 * Default VAT rate in Israel
 */
export const DEFAULT_VAT_RATE = 17;

/**
 * Default Morning Integration Settings
 */
export const DEFAULT_MORNING_SETTINGS = {
  defaultDocumentType: MORNING_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT,
  defaultPaymentType: MORNING_PAYMENT_TYPES.CREDIT_CARD,
  vatIncluded: true,
  language: "he",
  currency: "ILS",
};

// ==============================================
// UTILITY FUNCTIONS
// ==============================================

/**
 * Formats amount as Israeli Shekel currency
 * @param {number} amount - Amount to format
 * @returns {string} - Formatted amount (e.g., "₪350.00")
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "₪0.00";
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
  }).format(amount);
};

/**
 * Gets document type options for select inputs
 * @returns {array} - Array of {value, label} objects
 */
export const getDocumentTypeOptions = () => {
  return [
    MORNING_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT,
    MORNING_DOCUMENT_TYPES.RECEIPT,
    MORNING_DOCUMENT_TYPES.TAX_INVOICE,
    MORNING_DOCUMENT_TYPES.CREDIT_INVOICE,
  ].map((value) => ({ value, label: MORNING_DOCUMENT_LABELS[value] }));
};

/**
 * Document-type options filtered to what the business may issue
 * (`morningIntegration.allowedDocumentTypes` from GET /documents/types).
 * Empty / unknown list → all options.
 * @param {number[]|null|undefined} allowedIds
 */
export const getAllowedDocumentTypeOptions = (allowedIds, exempt = false) => {
  let all = getDocumentTypeOptions();
  // עוסק פטור cannot issue VAT invoices (Morning 2403) even though
  // /documents/types may still list them. Hide 305/320 up front.
  if (exempt) {
    all = all.filter(
      (o) =>
        o.value !== MORNING_DOCUMENT_TYPES.TAX_INVOICE &&
        o.value !== MORNING_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT
    );
  }
  if (!Array.isArray(allowedIds) || allowedIds.length === 0) return all;
  const set = new Set(allowedIds.map(Number));
  const filtered = all.filter((o) => set.has(o.value));
  return filtered.length > 0 ? filtered : all;
};

/** True when the Morning business is VAT-exempt (עוסק פטור). */
export const isMorningExempt = (morningIntegration) =>
  morningIntegration?.exemption === true ||
  morningIntegration?.businessType === MORNING_BUSINESS_TYPES.OSEK_PATUR;

/**
 * The document type a clinic really wants, mirroring the server remap in
 * functions/morningPayload.js. Settings without `settingsVersion` were saved
 * under swapped labels (305 ↔ 320); v2+ values are trusted verbatim.
 * Use for the INITIAL select value only; the server re-resolves on create.
 */
export const resolveClinicDefaultDocumentType = (morningIntegration) => {
  const mi = morningIntegration || {};
  const raw = Number(mi.defaultDocumentType) || MORNING_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT;
  if ((Number(mi.settingsVersion) || 1) >= MORNING_SETTINGS_VERSION) return raw;
  if (raw === 305) {
    return isMorningExempt(mi)
      ? MORNING_DOCUMENT_TYPES.RECEIPT
      : MORNING_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT;
  }
  if (raw === 320) return MORNING_DOCUMENT_TYPES.TAX_INVOICE;
  return raw;
};

/** Recommended default for a business (new connections). */
export const getRecommendedDocumentType = (morningIntegrationOrBusinessInfo) => {
  const src = morningIntegrationOrBusinessInfo || {};
  const exempt =
    src.isExempt === true ||
    src.exemption === true ||
    src.businessType === MORNING_BUSINESS_TYPES.OSEK_PATUR;
  return exempt ? MORNING_DOCUMENT_TYPES.RECEIPT : MORNING_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT;
};

/**
 * Which blocks a document type needs (mirror of server `documentNeeds`).
 * 305 is a tax invoice WITHOUT a payment row; 400 is a receipt WITHOUT income rows.
 */
export const getDocumentNeeds = (type, morningIntegration) => {
  const t = Number(type);
  if (t === MORNING_DOCUMENT_TYPES.RECEIPT) {
    return { income: morningIntegration?.receiptIncomeEnabled === true, payment: true };
  }
  if (t === MORNING_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT) return { income: true, payment: true };
  return { income: true, payment: false };
};

/** Morning `payment[].appType` for payment-app rows (type 10). */
export const getMorningAppType = (paymentMethod) => {
  const mapping = { bit: 1, paybox: 3, googlepay: 5, applepay: 6 };
  return mapping[String(paymentMethod || "").toLowerCase()] || 1;
};

/**
 * Gets payment type options for select inputs
 * @returns {array} - Array of {value, label} objects
 */
export const getPaymentTypeOptions = () => {
  return [
    { value: MORNING_PAYMENT_TYPES.CASH, label: "מזומן" },
    { value: MORNING_PAYMENT_TYPES.CREDIT_CARD, label: "כרטיס אשראי" },
    { value: MORNING_PAYMENT_TYPES.BIT, label: "אפליקציה (ביט/פייבוקס/אחר)" },
    { value: MORNING_PAYMENT_TYPES.BANK_TRANSFER, label: "העברה בנקאית" },
    { value: MORNING_PAYMENT_TYPES.CHECK, label: "צ'ק" },
    { value: MORNING_PAYMENT_TYPES.OTHER, label: "אחר" },
  ];
};

/**
 * Gets business type options for select inputs
 * @returns {array} - Array of {value, label} objects
 */
export const getBusinessTypeOptions = () => {
  return [
    { value: MORNING_BUSINESS_TYPES.OSEK_MORSHE, label: "עוסק מורשה" },
    { value: MORNING_BUSINESS_TYPES.OSEK_PATUR, label: "עוסק פטור" },
    { value: MORNING_BUSINESS_TYPES.COMPANY, label: "חברה בע״מ" },
    { value: MORNING_BUSINESS_TYPES.AMUTA, label: "עמותה" },
  ];
};

/**
 * Maps CliniKit payment method to Morning payment type
 * @param {string} paymentMethod - CliniKit payment method (cash, credit, bit, transfer, check)
 * @returns {number} - Morning payment type code
 */
export const mapPaymentMethodToMorning = (paymentMethod) => {
  const mapping = {
    cash: MORNING_PAYMENT_TYPES.CASH,
    credit: MORNING_PAYMENT_TYPES.CREDIT_CARD,
    bit: MORNING_PAYMENT_TYPES.BIT,
    transfer: MORNING_PAYMENT_TYPES.BANK_TRANSFER,
    check: MORNING_PAYMENT_TYPES.CHECK,
  };
  return mapping[paymentMethod] || MORNING_PAYMENT_TYPES.OTHER;
};
